import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n";
import { AuthProvider } from "@/auth";
import Layout from "@/components/Layout";
// `ScanCat` (the heavy live-camera flow) is intentionally NOT imported —
// it is kept on disk so we can swap it back in one line when AI returns.
// The `/scan-cat` route now renders `ScanCatPhotoPicker` (Capacitor +
// HTML file-input fallback, iOS WebView safe).
import ErrorBoundary from "@/components/ErrorBoundary";

// Pages load when they are opened, not when the app boots.
//
// All 35 of these were imported eagerly, so every visitor downloaded the entire
// application before the landing page could paint: the MapLibre engine (~800 KB
// on its own), the 2,500-line camera flow, the charting library, and thirty
// screens they would never open. The app did not get slower because any one page
// got slower — it got slower because the bundle kept growing and everyone paid
// for all of it, up front, on a phone, on mobile data.
//
// Landing, Login and Register stay eager: they are the front door, and a spinner
// there costs more than the bytes it saves.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";

// Yeni bir sürüm deploy edilince tarayıcıda kalan eski `main.js` artık var
// olmayan chunk hash'lerini istemeye çalışır ve "Loading chunk N failed" hatası
// verir. lazyRetry bunu yakalar: 10 sn'de en fazla bir kez sayfayı sessizce
// yeniler (taze index.html + yeni hash'ler gelir). Döngüye girmemek için zaman
// damgasıyla korunur — kalıcı bir bozuklukta tek sefer yenileyip hatayı gösterir.
function lazyRetry(factory) {
  return lazy(() =>
    factory().catch((err) => {
      try {
        const now = Date.now();
        const last = Number(sessionStorage.getItem("chunkReloadAt") || 0);
        if (now - last > 10000) {
          sessionStorage.setItem("chunkReloadAt", String(now));
          window.location.reload();
          return new Promise(() => {}); // yeniden yükleme sırasında beklet
        }
      } catch { /* sessionStorage yoksa yut */ }
      throw err;
    })
  );
}

const ForgotPassword = lazyRetry(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyRetry(() => import("@/pages/ResetPassword"));
const CatList = lazyRetry(() => import("@/pages/CatList"));
const CatVaccinations = lazyRetry(() => import("@/pages/CatVaccinations"));
const LostCat = lazyRetry(() => import("@/pages/LostCat"));
const FeedingDetail = lazyRetry(() => import("@/pages/FeedingDetail"));
const CatDetail = lazyRetry(() => import("@/pages/CatDetail"));
const CatEdit = lazyRetry(() => import("@/pages/CatEdit"));
const AddCat = lazyRetry(() => import("@/pages/AddCat"));
const AIIdentify = lazyRetry(() => import("@/pages/AIIdentify"));
const MapView = lazyRetry(() => import("@/pages/MapView"));
const Sightings = lazyRetry(() => import("@/pages/Sightings"));
const EmergencyDetail = lazyRetry(() => import("@/pages/EmergencyDetail"));
const EmergencyReport = lazyRetry(() => import("@/pages/EmergencyReport"));
const VolunteerCall = lazyRetry(() => import("@/pages/VolunteerCall"));
const FoodWaterCall = lazyRetry(() => import("@/pages/FoodWaterCall"));
const FoodWaterCallDetail = lazyRetry(() => import("@/pages/FoodWaterCallDetail"));
const InjuredCat = lazyRetry(() => import("@/pages/InjuredCat"));
const DangerZone = lazyRetry(() => import("@/pages/DangerZone"));
const Volunteers = lazyRetry(() => import("@/pages/Volunteers"));
const Messenger = lazyRetry(() => import("@/pages/Messenger"));
const Notifications = lazyRetry(() => import("@/pages/Notifications"));
const Feeding = lazyRetry(() => import("@/pages/Feeding"));
const AddFeeding = lazyRetry(() => import("@/pages/AddFeeding"));
const Dashboard = lazyRetry(() => import("@/pages/Dashboard"));
const Profile = lazyRetry(() => import("@/pages/Profile"));
const ProfileEdit = lazyRetry(() => import("@/pages/ProfileEdit"));
const HowItWorks = lazyRetry(() => import("@/pages/HowItWorks"));
const Help = lazyRetry(() => import("@/pages/Help"));
const About = lazyRetry(() => import("@/pages/About"));
const NotFound = lazyRetry(() => import("@/pages/NotFound"));
const MyPetCats = lazyRetry(() => import("@/pages/MyPetCats"));
const MyPetCatDetail = lazyRetry(() => import("@/pages/MyPetCatDetail"));
const Leaderboard = lazyRetry(() => import("@/pages/Leaderboard"));
const Premium = lazyRetry(() => import("@/pages/Premium"));
const PremiumSuccess = lazyRetry(() => import("@/pages/PremiumSuccess"));
const PremiumCancel = lazyRetry(() => import("@/pages/PremiumCancel"));
const PremiumLounge = lazyRetry(() => import("@/pages/PremiumLounge"));
const LiveIdentify = lazyRetry(() => import("@/pages/LiveIdentify"));
const ScanCatPhotoPicker = lazyRetry(() => import("@/pages/ScanCatPhotoPicker"));
const ApplyVolunteer = lazyRetry(() => import("@/pages/ApplyVolunteer"));
const AdminApplications = lazyRetry(() => import("@/pages/AdminApplications"));
const AdminEmergencies = lazyRetry(() => import("@/pages/AdminEmergencies"));
const PhotoMonthly = lazyRetry(() => import("@/pages/PhotoMonthly"));
const Badges = lazyRetry(() => import("@/pages/Badges"));
const CatSafety = lazyRetry(() => import("@/pages/CatSafety"));
const IncidentFeedback = lazyRetry(() => import("@/pages/IncidentFeedback"));
const ObservationHouse = lazyRetry(() => import("@/pages/ObservationHouse"));
const Neighborhood = lazyRetry(() => import("@/pages/Neighborhood"));
const PrivacyPolicy = lazyRetry(() => import("@/pages/PrivacyPolicy"));
const Terms = lazyRetry(() => import("@/pages/Terms"));
const CommunityRules = lazyRetry(() => import("@/pages/CommunityRules"));
const UserProfile = lazyRetry(() => import("@/pages/UserProfile"));
const PawFriends = lazyRetry(() => import("@/pages/PawFriends"));



/** Sayfa parçası yüklenirken gösterilir.
 *
 * SESSİZ tutuyoruz (24 Tem 2026): eskiden ekranın %70'ini kaplayan boş bir
 * alan + spinner çiziyordu; sayfa geçişlerinde "arada bir sayfa belirip
 * kaybolmuş" gibi görünüyordu. Artık hiçbir şey çizmiyor — chunk'lar küçük,
 * geçiş anında sayfa yerinde kalır, hazır olunca yenisi gelir. Ekran
 * okuyucular için görünmez bir durum metni bırakıldı. */
/**
 * Rota değiştiğinde sayfayı başa al.
 *
 * Tek sayfa uygulamalarında tarayıcı, bağlantıya basıldığında kaydırma
 * konumunu SIFIRLAMAZ: A sayfasında aşağı kaydırıp B'ye geçtiğinde pencere
 * hâlâ aşağıda kalır, B'nin üstü görünmez ve sayfa "altı gitmiş" gibi durur.
 *
 * Ama her durumda başa almak da yanlış olurdu: GERİ tuşuna basıldığında
 * tarayıcının eski konumu geri yüklemesi İSTENEN davranıştır — uzun kedi
 * listesinde 40. kediye kadar inip bir profile girip geri döndüğünde en başa
 * fırlamak istemezsin.
 *
 * Bu yüzden yalnızca ileri yönlü gezinmede (PUSH/REPLACE) başa alıyoruz,
 * geri/ileri (POP) durumunda tarayıcıya karışmıyoruz.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType === "POP") return;
    window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

function RouteFallback() {
  return (
    <div className="sr-only" role="status" aria-live="polite">
      Yükleniyor…
    </div>
  );
}

function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ErrorBoundary scope="app-root">
          <Layout>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/cats" element={<CatList />} />
              <Route path="/cat-vaccinations" element={<CatVaccinations />} />
              <Route path="/lost-cat" element={<LostCat />} />
              <Route path="/cats/new" element={<AddCat />} />
              <Route path="/cats/:id" element={<CatDetail />} />
              <Route path="/cats/:id/edit" element={<CatEdit />} />
              <Route path="/identify" element={<AIIdentify />} />
              <Route path="/identify/live" element={<LiveIdentify />} />
              <Route path="/scan-cat" element={<ScanCatPhotoPicker />} />
              <Route path="/map" element={<MapView />} />
              <Route path="/sightings" element={<Sightings />} />
              <Route path="/emergencies" element={<Sightings />} />
              <Route path="/emergencies/:id" element={<EmergencyDetail />} />
              <Route path="/emergency-report" element={<EmergencyReport />} />
              <Route path="/volunteer-call" element={<VolunteerCall />} />
              <Route path="/food-water-call" element={<FoodWaterCall />} />
              <Route path="/food-water-calls/:id" element={<FoodWaterCallDetail />} />
              <Route path="/injured-cat" element={<InjuredCat />} />
              <Route path="/danger-zone" element={<DangerZone />} />
              <Route path="/volunteers" element={<Volunteers />} />
              <Route path="/messages" element={<Messenger />} />
              <Route path="/messages/:userId" element={<Messenger />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/feeding" element={<Feeding />} />
              <Route path="/feeding/new" element={<AddFeeding />} />
              <Route path="/feeding/:id" element={<FeedingDetail />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/profile" element={<ErrorBoundary scope="profile"><Profile /></ErrorBoundary>} />
              {/* The "Düzenle" button on the profile has pointed here all along.
                  The route did not exist, so it opened a blank page. */}
              <Route path="/profile/edit" element={<ErrorBoundary scope="profile-edit"><ProfileEdit /></ErrorBoundary>} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/premium" element={<Premium />} />
              <Route path="/premium/success" element={<PremiumSuccess />} />
              <Route path="/premium/cancel" element={<PremiumCancel />} />
              <Route path="/premium/lounge" element={<PremiumLounge />} />
              <Route path="/apply-volunteer" element={<ApplyVolunteer />} />
              <Route path="/admin/applications" element={<AdminApplications />} />
              <Route path="/admin/emergencies" element={<AdminEmergencies />} />
              <Route path="/photos/monthly" element={<PhotoMonthly />} />
              <Route path="/badges" element={<Badges />} />
              <Route path="/safety" element={<CatSafety />} />
              <Route path="/observation-house" element={<ObservationHouse />} />
              <Route path="/neighborhood" element={<Neighborhood />} />
              <Route path="/gizlilik-politikasi" element={<PrivacyPolicy />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              {/* App Review, Guideline 1.2: a reviewer must be able to reach these.
                  The pages existed and were unreachable, which is the same as not
                  existing — the last rejection was for exactly this. */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/community-rules" element={<CommunityRules />} />
              <Route path="/u/:userId" element={<UserProfile />} />
              <Route path="/pati-arkadaslarim" element={<PawFriends />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              {/* Three links sat on the profile page pointing at nothing.
                  A dead link is not a missing page — it is a broken app. */}
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/help" element={<Help />} />
              <Route path="/about" element={<About />} />
              {/* Evcil Kedilerim — a person's own cats, kept out of the street
                  registry. Reached from a card on the profile. */}
              <Route path="/evcil-kedilerim" element={<MyPetCats />} />
              <Route path="/evcil-kedilerim/:id" element={<MyPetCatDetail />} />
              <Route path="/incidents/:id/feedback" element={<IncidentFeedback />} />
              {/* Catch-all, and it must stay LAST. Without it React Router
                  matched nothing and rendered nothing: a white page under the
                  navigation bar. Every mistyped URL and every dead link landed
                  there in silence. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            </Suspense>
          </Layout>
          </ErrorBoundary>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </I18nProvider>
  );
}

export default App;
